import { useBetterFeatureClient } from '@di-devs/utils-fe'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import {
	initialRichMenuSettingsState,
	richMenuReducer,
} from '@/components/admin/Settings/RichMenu/RichMenuReducer'
import type { FeatureClient } from '@/config/client'
import { useRichMenuTabGroupsQuery, useRichMenuTemplatesQuery } from '@/queries'
import { COMMONS } from '@/utils'
import {
	RICHMENU_ACTIONS,
	type RichMenuSettingsAction,
	type RichMenuSettingsContext,
	type RichMenuTabGroup,
} from './RichMenuAction'

/**
 * House state of rich menus and reference to dispatch method
 */
const initialContext: RichMenuSettingsContext = {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	dispatch: (value: RichMenuSettingsAction) => {},
	state: initialRichMenuSettingsState,
}
const RichMenuSettingsCtx = createContext(initialContext)

export const useRichMenuSettingsContext = () => {
	const richMenusCtx = useContext(RichMenuSettingsCtx)
	if (!richMenusCtx) {
		throw new Error('RichMenuSettingsContext: No value provided')
	}

	return richMenusCtx
}

const isPublishedDefaultRichMenuTabGroup = (tabGroup: RichMenuTabGroup) =>
	tabGroup.displayPriority === 'DEFAULT' && tabGroup.status === 'PUBLISHED'
const isPublishedPerUserRichMenuTabGroup = (tabGroup: RichMenuTabGroup) =>
	tabGroup.displayPriority === 'USER' && tabGroup.status === 'PUBLISHED'
const isDraftRichMenuTabGroup = (tabGroup: RichMenuTabGroup) => tabGroup.status === 'DRAFT'

const sortRichMenusByTabIndex = (tabGroup: RichMenuTabGroup): RichMenuTabGroup => {
	const before = (tabGroup.richmenus ?? []).map((r) => `${r.name}(tabIndex:${r.tabIndex})`)
	const sorted = [...(tabGroup.richmenus ?? [])].sort(
		(a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
	)
	const after = sorted.map((r) => `${r.name}(tabIndex:${r.tabIndex})`)
	console.log(`[RichMenu sort] group ${tabGroup.groupId} BEFORE:`, before.join(', '))
	console.log(`[RichMenu sort] group ${tabGroup.groupId} AFTER: `, after.join(', '))
	return { ...tabGroup, richmenus: sorted }
}

export const RichMenuSettingsProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(richMenuReducer, initialRichMenuSettingsState)
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>()
	const templatesQuery = useRichMenuTemplatesQuery(reactFeatureClient)
	const tabGroupsQuery = useRichMenuTabGroupsQuery(reactFeatureClient)

	const findDefaultTabGroup = useCallback(
		() => tabGroupsQuery?.data?.data?.data?.find(isPublishedDefaultRichMenuTabGroup),
		[tabGroupsQuery?.data?.data?.data],
	)
	const findPerUserTabGroup = useCallback(
		() => tabGroupsQuery?.data?.data?.data?.find(isPublishedPerUserRichMenuTabGroup),
		[tabGroupsQuery?.data?.data?.data],
	)
	const findDraftTabGroups = useCallback(
		() => tabGroupsQuery?.data?.data?.data?.filter(isDraftRichMenuTabGroup),
		[tabGroupsQuery?.data?.data?.data],
	)

	useEffect(() => {
		dispatch({
			type: RICHMENU_ACTIONS.SET,
			payload: {
				templates: templatesQuery?.data?.data?.data,
			},
		})
	}, [templatesQuery?.data?.data?.data])

	useEffect(() => {
		dispatch({
			type: RICHMENU_ACTIONS.SET,
			payload: {
				tabGroups: tabGroupsQuery?.data?.data?.data?.map(sortRichMenusByTabIndex),
				defaultTabGroup:
					findDefaultTabGroup() && sortRichMenusByTabIndex(findDefaultTabGroup()!),
				perUserTabGroup:
					findPerUserTabGroup() && sortRichMenusByTabIndex(findPerUserTabGroup()!),
				draftTabGroups: findDraftTabGroups()
					?.map(sortRichMenusByTabIndex)
					.sort((a: RichMenuTabGroup, b: RichMenuTabGroup) =>
						COMMONS.compareDateTime(b.updatedAt, a.updatedAt),
					),
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabGroupsQuery?.data?.data?.data])

	// Memoize and replace initialized dispatch placeholder with actual method
	const memoizeValues = useMemo(
		() => ({
			dispatch: dispatch,
			state: state,
		}),
		[dispatch, state],
	)

	return (
		<RichMenuSettingsCtx.Provider value={memoizeValues}>
			{children}
		</RichMenuSettingsCtx.Provider>
	)
}
